import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";

import {
  HomeOutlined as HomeIcon,
  LocalAtmOutlined as LocalAtmOutlinedIcon,
  HistoryOutlined as HistoryOutlinedIcon,
  PeopleAltOutlined as PeopleAltOutlinedIcon,
  DateRangeOutlined as DateRangeOutlinedIcon,
  FlightTakeoffOutlined as FlightTakeoffOutlinedIcon,
  CreditCard as CreditCardIcon,
  SettingsOutlined as SettingsIcon,
  AccountBalanceWalletOutlined as AccountBalanceWalletOutlinedIcon,
  ForumOutlined as ForumOutlinedIcon,
  PersonOutlineOutlined as PersonOutlineOutlinedIcon,
  GroupAddOutlined as GroupAddOutlinedIcon,
  ExploreOutlined as ExploreOutlinedIcon,
  AttachMoneyOutlined as AttachMoneyOutlinedIcon,
  ManageAccountsOutlined as ManageAccountsOutlinedIcon,
  HelpOutline as HelpOutlineIcon,
  ReviewsOutlined as ReviewsOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  LanguageOutlined as LanguageOutlinedIcon,
  CardGiftcardOutlined as CardGiftcardOutlinedIcon,
} from "@mui/icons-material";

import { usePermissions } from "components/Hook";
import { getEmployeeDetails } from "selectors/employee";

import NavigationRoutes from "constants/NavigationRoutes";

import { REFERRAL_URLS } from "constants";

const EMPTY_CONVERSATIONS = [];

export const useMenuItems = () => {
  const userAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.userAdditionalInfo);
  const employeePeers = useSelector((state) => state.EmployeePeers);
  const { loggedInUser, loggedInUserType } = usePermissions();
  const {
    anniversaryBasedTimeOff,
    customizedInvoicing,
    holidayCalendarRevamp,
    checkinCheckout,
    talentNetworkCustomHireFlag,
  } = useFlags();
  const employeeDetails = useSelector(getEmployeeDetails);
  const isClockInEnabledForEmployee = employeeDetails?.isClockInAllowed === true;
  const chatConversations = useSelector((state) => state.Chat?.conversations ?? EMPTY_CONVERSATIONS);
  const totalChatUnread = useMemo(
    () => chatConversations.filter((conv) => conv.unreadCount > 0).length,
    [chatConversations]
  );

  return useCallback(() => {
    const {
      dashboard,
      payroll,
      payrollHistory,
      workforce,
      timeoffLeave,
      holidays,
      payment,
      settings,
      paystubs,
      edgeWallet,
      timeoff,
      peers,
      // community,
      eprofile,
      customers,
      workforcePayrolls,
      payrollsUpload,
      timeoffAdmin,
      profile,
      talentNetwork,
      timesheet,
      chat,
    } = NavigationRoutes;

    const items = [
      {
        id: "home",
        title: "Home",
        path: dashboard,
        icon: <HomeIcon />,
        category: "default",
      },
    ];

    if (loggedInUserType === "customer") {
      const canAccessFinancials =
        Array.isArray(loggedInUser?.permissions) &&
        loggedInUser.permissions.some((p) => p === "CustomerAdmin" || p === "CustomerEmployee");

      if (canAccessFinancials) {
        items.push({
          id: "payroll",
          title: "Payroll information",
          path: payroll,
          icon: <LocalAtmOutlinedIcon />,
          category: "Payroll",
        });
        items.push({
          id: "payroll-history",
          title: customizedInvoicing ? "Payment history" : "Payroll history",
          path: payrollHistory,
          icon: <HistoryOutlinedIcon />,
          category: "Payroll",
        });
      }

      items.push(
        {
          id: "workforce",
          title: "Workforce",
          path: workforce,
          icon: <PeopleAltOutlinedIcon />,
          category: "Workforce",
        },
        {
          id: "timeoff",
          title: "Time-off",
          path: timeoffLeave,
          icon: <FlightTakeoffOutlinedIcon />,
          category: "Workforce",
          divider: true,
        }
      );

      if (canAccessFinancials) {
        items.push({
          id: "payment-methods",
          title: "Payment method",
          path: payment,
          icon: <CreditCardIcon />,
          category: "Account",
        });
      }

      items.push({
        id: "chat",
        title: "Chat",
        path: chat,
        icon: <ForumOutlinedIcon />,
        category: "Communication",
        divider: true,
        notificationCount: totalChatUnread || undefined,
      });

      if (!holidayCalendarRevamp) {
        items.push({
          id: "holidays",
          title: "Holiday calendar",
          path: holidays,
          icon: <DateRangeOutlinedIcon />,
          category: "Workforce",
        });
      }

      if (holidayCalendarRevamp) {
        items.push({
          id: "holiday-schedule",
          title: "Holiday schedule",
          path: holidays,
          icon: <DateRangeOutlinedIcon />,
          category: "Workforce",
        });
      }

      if (loggedInUser?.permissions?.includes("CustomerAdmin")) {
        items.push({
          id: "account-settings",
          title: "Account settings",
          path: settings,
          icon: <SettingsIcon />,
          category: "Account",
          divider: true,
        });
        if (talentNetworkCustomHireFlag && userAdditionalInfo?.talentNetworkVisibility) {
          items.push({
            id: "talent-network",
            title: "Talent Network",
            path: talentNetwork,
            icon: <LanguageOutlinedIcon />,
            category: "Discover talent",
          });
          items.push({
            id: "request-a-hire",
            title: "Request a Hire",
            path: NavigationRoutes.requestAHire,
            icon: <GroupAddOutlinedIcon />,
            category: "Discover talent",
            divider: true,
          });
        }
      }
    } else if (loggedInUserType === "employee") {
      items.push({
        id: "paystubs",
        title: "Pay slips",
        path: paystubs,
        icon: <LocalAtmOutlinedIcon />,
        category: "Payroll",
      });

      if (userAdditionalInfo?.allowedForPayoneer) {
        items.push({
          id: "edge-wallet",
          title: "Edge wallet",
          path: edgeWallet,
          icon: <AccountBalanceWalletOutlinedIcon />,
          category: "Payroll",
        });
      }

      if (
        (userAdditionalInfo?.customerId || userAdditionalInfo?.pendingCustomerId) &&
        userAdditionalInfo?.timeoffFlag
      ) {
        items.push({
          id: "timeoff",
          title: "Time-off",
          path: timeoff,
          icon: <FlightTakeoffOutlinedIcon />,
          category: "Time-off & holidays",
          divider: true,
        });
      }

      items.push({
        id: "holidays",
        title: "Holiday calendar",
        path: holidays,
        icon: <DateRangeOutlinedIcon />,
        category: "Time-off & holidays",
      });

      if (checkinCheckout && isClockInEnabledForEmployee) {
        items.push({
          id: "timesheet",
          title: "Timesheet",
          path: timesheet,
          icon: <DateRangeOutlinedIcon />,
          category: "Time-off & holidays",
        });
      }

      const { customerId } = userAdditionalInfo || {};
      const { count } = employeePeers?.peers || {};

      if (customerId && count > 0) {
        items.push({
          id: "peers",
          title: "Team",
          path: peers,
          icon: <PeopleAltOutlinedIcon />,
          category: "Team",
        });
      }
      // IMPORTANT: temporarily disabled community menu item  PRD-382
      // items.push({
      //   id: "community",
      //   title: "Community",
      //   path: community,
      //   icon: <ForumOutlinedIcon />,
      //   category: "Team",
      //   divider: true,
      //   openInNewTab: true,
      // });

      items.push(
        {
          id: "chat",
          title: "Chat",
          path: chat,
          icon: <ForumOutlinedIcon />,
          category: "Communication",
          divider: true,
          notificationCount: totalChatUnread || undefined,
        },
        {
          id: "profile",
          title: "Profile",
          path: eprofile,
          icon: <PersonOutlineOutlinedIcon />,
          category: "Account",
        },

        {
          id: "referrals",
          title: "Referrals",
          path: REFERRAL_URLS[userAdditionalInfo?.country],
          icon: <GroupAddOutlinedIcon />,
          category: "Edge resources",
          openInNewTab: true,
        }
      );
    } else if (loggedInUserType === "edge") {
      items.push(
        {
          id: "workforce",
          title: "Workforce",
          path: workforce,
          icon: <PeopleAltOutlinedIcon />,
          category: "Workforce & Customers",
        },
        {
          id: "customers",
          title: "Customers",
          path: customers,
          icon: <CorporateFareOutlinedIcon />,
          category: "Workforce & Customers",
          divider: true,
        },
        {
          id: "chat",
          title: "Chat",
          path: chat,
          icon: <ForumOutlinedIcon />,
          category: "Communication",
          notificationCount: totalChatUnread || undefined,
        }
      );

      if (loggedInUser?.permissions?.includes("EdgeAdmin")) {
        items.push(
          {
            id: "finances",
            title: "Finances",
            path: workforcePayrolls,
            icon: <AttachMoneyOutlinedIcon />,
            category: "Payroll",
          },
          {
            id: "payrolls-upload",
            title: "Upload payroll",
            path: payrollsUpload,
            icon: <LocalAtmOutlinedIcon />,
            category: "Payroll",
            divider: true,
          }
        );
      }

      items.push({
        id: "timeoff",
        title: "Time-off",
        path: timeoffAdmin,
        icon: <FlightTakeoffOutlinedIcon />,
        category: "Time-off",
      });

      if (loggedInUser?.permissions?.includes("EdgeAdmin")) {
        if (!anniversaryBasedTimeOff) {
          items.push({
            id: "account-settings",
            title: "Manage users",
            path: settings,
            icon: <ManageAccountsOutlinedIcon />,
            category: "Account",
          });
        }
        if (anniversaryBasedTimeOff) {
          items.push({
            id: "profile",
            title: "Profile",
            path: profile,
            icon: <PersonOutlineOutlinedIcon />,
            category: "Profile & settings",
          });

          items.push({
            id: "account-settings",
            title: "Settings",
            path: settings,
            icon: <SettingsOutlinedIcon />,
            category: "Profile & settings",
          });
        }
      }

      if (!anniversaryBasedTimeOff) {
        items.push({
          id: "profile",
          title: "Profile",
          path: profile,
          icon: <PersonOutlineOutlinedIcon />,
          category: "Account",
        });
      }
    }

    return items;
  }, [
    loggedInUserType,
    loggedInUser,
    userAdditionalInfo,
    employeePeers,
    holidayCalendarRevamp,
    customizedInvoicing,
    anniversaryBasedTimeOff,
    checkinCheckout,
    totalChatUnread,
  ]);
};

export const useStickyItems = () => {
  const { loggedInUserType } = usePermissions();
  const platformConfig = useSelector((state) => state.PlatformConfig.platformConfig);
  const userAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.userAdditionalInfo);

  const shouldShowEdgeAtlas = useMemo(() => {
    const edgeAtlasLinks = platformConfig?.payload?.EDGE_ATLAS_NOTION_LINKS;

    if (!edgeAtlasLinks) {
      return false;
    }

    let config;
    try {
      config = typeof edgeAtlasLinks === "string" ? JSON.parse(edgeAtlasLinks) : edgeAtlasLinks;
    } catch (error) {
      console.error("Failed to parse EDGE_ATLAS_NOTION_LINKS:", error);
      return false;
    }

    if (loggedInUserType === "employee") {
      const talentConfig = config?.talent;
      if (!Array.isArray(talentConfig)) return false;

      const employeeCountry = userAdditionalInfo?.country;
      const countryEntry = talentConfig.find((item) => item.country === employeeCountry);

      if (countryEntry?.url) return true;

      const defaultEntry = talentConfig.find((item) => item.country === null);
      return !!defaultEntry?.url;
    }

    if (loggedInUserType === "customer") {
      const customerConfig = config?.customer;
      if (!Array.isArray(customerConfig)) return false;

      const defaultEntry = customerConfig.find((item) => item.industry === null);
      return !!defaultEntry?.url;
    }

    return false;
  }, [loggedInUserType, userAdditionalInfo, platformConfig]);

  return useCallback(() => {
    const { helpSupport, feedback, edgeAtlas, referrals } = NavigationRoutes;
    const items = [];

    if (loggedInUserType === "customer" || loggedInUserType === "employee") {
      if (shouldShowEdgeAtlas) {
        items.push({
          id: "edge-atlas",
          title: "Edge Atlas",
          path: edgeAtlas,
          icon: <ExploreOutlinedIcon />,
          openInNewTab: false,
        });
      }

      items.push({
        id: "help",
        title: "Help & support",
        path: helpSupport,
        icon: <HelpOutlineIcon />,
        openInNewTab: true,
      });

      if (loggedInUserType === "customer") {
        items.push({
          id: "referrals",
          title: "Refer a business",
          path: referrals,
          icon: <CardGiftcardOutlinedIcon />,
        });
      }

      items.push({
        id: "feedback",
        title: "Feedback",
        path: feedback,
        icon: <ReviewsOutlinedIcon />,
      });
    }

    return items;
  }, [loggedInUserType, shouldShowEdgeAtlas]);
};

export default useMenuItems;
